import React, { useState } from "react";
import NewOutwardProductModal from "./NewOutwardProductmodal";
import NewOutwardMaterialModal from "./NewOutwardMaterialModal";

interface OutwardStarterModalProps {
  isOpen: boolean;
  requestId: string;
  requestNumber: string;
  type: "product" | "material" | null | undefined;
  onClose: (success: boolean) => void;
}

const OutwardStarterModal: React.FC<OutwardStarterModalProps> = ({
  isOpen,
  onClose,
  requestId,
  requestNumber,
  type,
}) => {
  const [selectedType, setSelectedType] = useState<
    "product" | "material" | null
  >(null);

  if (!isOpen) return null;

  const resolvedType = type ?? selectedType;

  if (resolvedType === "product") {
    return (
      <NewOutwardProductModal
        isOpen={isOpen}
        defaultRequestId={requestId}
        requestNumber={requestNumber}
        onClose={onClose}
      />
    );
  }

  if (resolvedType === "material") {
    return (
      <NewOutwardMaterialModal
        isOpen={isOpen}
        requestId={requestId}
        requestNumber={requestNumber}
        onClose={onClose}
      />
    );
  }

  // No type provided yet, show the starter selector page
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[110] transition-opacity">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 animate-scale-in">
        {/* MODAL HEADER */}
        <div className="bg-slate-50 px-6 py-5 flex justify-between items-center border-b border-slate-200/80">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">
              Floor Outward Operations
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Select an outward dispatch flow to proceed
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors focus:outline-none"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* MODAL BODY / OPTIONS GRID */}
        <div className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5 bg-white">
          {/* OPTION 1: OUTWARD PRODUCT */}
          <div
            onClick={() => setSelectedType("product")}
            className="group relative bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-500 transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1.5 bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />

            <div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>

              <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                Outward Product
              </h3>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                Record production completion, assign unit serial numbers, and
                outward finished goods stock.
              </p>
            </div>

            <div className="mt-6 flex items-center text-xs font-semibold text-blue-600 group-hover:translate-x-1 transition-transform">
              <span>Select Flow</span>
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </div>
          </div>

          {/* OPTION 2: OUTWARD MATERIAL */}
          <div
            onClick={() => setSelectedType("material")}
            className="group relative bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-emerald-500 transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1.5 bg-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />

            <div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              </div>

              <h3 className="text-base font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                Outward Material
              </h3>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                Transfer raw materials or return allocated store items back to
                main inventory or vendors.
              </p>
            </div>

            <div className="mt-6 flex items-center text-xs font-semibold text-emerald-600 group-hover:translate-x-1 transition-transform">
              <span>Select Flow</span>
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* MODAL FOOTER */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200/80 flex justify-end">
          <button
            onClick={() => onClose(false)}
            className="px-5 py-2.5 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold rounded-xl text-xs transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutwardStarterModal;
